import {
  cannibalizationVerdictsForPage,
  competingPagesPayload,
  competingPairVerdicts
} from './cannibalization-card';
import {
  COMPETING_SEARCH_MAX_WORDS,
  competingObservationKey,
  searchRunsAgainstPageDirection,
  type CompetingEvidenceSource,
  type CompetingPagesResult
} from './competing-pages';
import { ESTIMATED_SEARCH_EVIDENCE_WARNING } from './estimated-evidence';
import { stemTopicToken } from './topic-supporting-pages';

/**
 * The searches a page is close on (quality sprint 8, workstream B; NEAR_MISS_SEARCHES.md §3). The
 * pure rules: which of a page's searches are near misses, which of those are noise, which belong to
 * the Cannibalization card, and where the search's words sit on our page. The data is gathered
 * read-only by `backend/src/services/near-miss-searches.ts`.
 *
 * Nothing here decides the answer a writer sees (wording, a section, or nothing): the card does
 * that from `location`. Nothing here calls a model (owner, 2026-10-19): Search Console rows or
 * ranked keywords, and our stored page text.
 *
 * One evidence source per domain, never mixed (owner, 2026-10-20, as `competing-pages.ts`): Search
 * Console for every page of a connected domain, the estimated ranked keywords otherwise, every item
 * then marked estimated.
 */

export type NearMissSource = CompetingEvidenceSource;

/**
 * Best average position that is still a near miss. Positions 1 to 3 are already where the clicks
 * are (findings §35: on the site with Search Console, the click rate falls steeply below #3); the
 * band is the spec's (NEAR_MISS_SEARCHES §3), not fitted.
 */
export const NEAR_MISS_MIN_POSITION = 4;

/** Worst position that is still a near miss: the end of page two. The spec's band, not fitted. */
export const NEAR_MISS_MAX_POSITION = 20;

/**
 * Search Console only: impressions over the stored window (`GSC_PRIMARY_QUERY_WINDOW_DAYS`, 90 days)
 * below which a search is too thin to be worth a writer's change. The direction test's floor
 * (findings §35), and the one the sprint 8 labelled set was drawn with, so the labels and the engine
 * see the same candidates; the labels hold nothing under it, so it is not fitted. Sprint 8 frozen
 * data, ManyRequests' 29 queue pages: 1,725 searches in the band at any demand, 355 at 30 or more
 * (site-wide, 416 pages: 12,430 and 2,159).
 */
export const NEAR_MISS_MIN_IMPRESSIONS = 30;

/**
 * Estimates only: monthly search volume of a ranked keyword below which it is too thin. The direction
 * test's and the labelled set's floor, not fitted. Sprint 8 frozen data, SlideSpeak's 20 queue pages:
 * 355 ranked keywords in the band, 230 at 50 or more (site-wide, 797 analysed pages: 2,640 and 1,831).
 */
export const NEAR_MISS_MIN_SEARCH_VOLUME = 50;

/** Why an in-band search over the floor is not shown. One reason per search: the first in this order. */
export type NearMissRemovalReason =
  /** Not a person's search for this page: a search operator, a site our page never names, digits only, a keyword string. */
  | 'spam'
  /** A long, chat-style instruction or question typed into an AI assistant and logged as a search. */
  | 'ai_prompt'
  /** Not in English: another script, letters with accents, or another language's own small words. */
  | 'other_language'
  /** A year the page does not carry: a dated variant of a search. */
  | 'year_variant'
  /** A country, city or region the page does not name: a local variant of a search. */
  | 'place_variant'
  /**
   * The search is the title or H1 itself, word for word in any order, on a page that offers the thing
   * ("sample / template / free / example / online" counted as the title's own words): the page already
   * answers it where Google weighs it.
   */
  | 'title_match'
  /** A "sample / template / free / example / online" variant of words the title or H1 already holds. */
  | 'answered_variant'
  /**
   * A shared search of a pair the Cannibalization card shows (sprint 7's card decision): that card owns
   * it (owner Q5). Shared searches of pairs no card shows stay here: nobody else answers them.
   */
  | 'shared_with_our_page';

export const NEAR_MISS_REMOVAL_REASONS: readonly NearMissRemovalReason[] = [
  'spam',
  'ai_prompt',
  'other_language',
  'year_variant',
  'place_variant',
  'title_match',
  'answered_variant',
  'shared_with_our_page'
];

/** One search the page appears for, as the loader read it. */
export interface NearMissSearchInput {
  search: string;
  /** Search Console: impression-weighted average over the window. Estimates: the stored position. */
  position: number | null;
  /** Search Console only (null on estimates). */
  impressions: number | null;
  clicks: number | null;
  /** Estimates only: monthly search volume (null on Search Console). */
  searchVolume: number | null;
}

/** One block of our page's own text: a heading and the text under it, our site template removed. */
export interface NearMissPageBlock {
  heading: string;
  /** 1 for the H1 block, 2 for an H2…; null when unknown. */
  level: number | null;
  body: string;
}

/** What the location check reads from our page. */
export interface NearMissPageText {
  /** The title tag, as stored (the site suffix left on: it is part of what Google reads). */
  title: string | null;
  h1: string | null;
  /** Main-content blocks in page order, our site template removed. The first is the opening. */
  blocks: NearMissPageBlock[];
}

/**
 * Where the search's words sit on our page.
 *
 * - `heading`: every word in one place Google weighs: the title, the H1 or one heading.
 * - `opening`: not in a heading; every word in the page's first block (heading and text).
 * - `one_section`: every word inside one section (its heading and text), not the opening.
 * - `spread`: every word is on the page, but no one section holds them all.
 * - `not_on_page`: at least one word is nowhere in our text (`missingWords`).
 */
export type NearMissWordPlace = 'heading' | 'opening' | 'one_section' | 'spread' | 'not_on_page';

export interface NearMissWordLocation {
  place: NearMissWordPlace;
  /** For `heading`: which one, and its text. */
  heading: { kind: 'title' | 'h1' | 'heading'; text: string } | null;
  /** For `opening` and `one_section`: the section's heading ('' when the block has none). */
  section: string | null;
  /** The search, word for word and in order, is already in the title, the H1 or a heading. */
  phraseInHeading: boolean;
  /** For `not_on_page`: the search's words that are nowhere on our page. */
  missingWords: string[];
}

/** One near-miss search, kept or removed, with its numbers and where its words sit. */
export interface NearMissSearch {
  search: string;
  source: NearMissSource;
  /** True on every item built without Search Console. */
  estimated: boolean;
  position: number;
  impressions: number | null;
  clicks: number | null;
  searchVolume: number | null;
  /** Impressions (Search Console) or monthly search volume (estimates): what the list is ordered by. */
  demand: number;
  kept: boolean;
  /** The first removal rule that applies; null when kept. */
  reason: NearMissRemovalReason | null;
  /** Every removal rule that applies, in `NEAR_MISS_REMOVAL_REASONS` order. */
  reasons: NearMissRemovalReason[];
  location: NearMissWordLocation;
}

export interface NearMissPageResult {
  source: NearMissSource;
  estimated: boolean;
  /** `ESTIMATED_SEARCH_EVIDENCE_WARNING` on estimates; null with Search Console. */
  warning: string | null;
  /** Our page's title and H1 as the location check read them: the card's answers read them too. */
  title: string | null;
  h1: string | null;
  /** The main-content headings, our template removed, in page order: the card reads how-to headings. */
  headings: string[];
  /** Searches in the position band, at any demand. */
  inBand: number;
  /** In band and over the demand floor, most demand first: kept and removed. */
  searches: NearMissSearch[];
  /** Per removal reason, the searches it removed (the first reason only). */
  removedBy: Record<NearMissRemovalReason, number>;
  kept: number;
}

export interface NearMissInput {
  source: NearMissSource;
  searches: readonly NearMissSearchInput[];
  page: NearMissPageText;
  /**
   * Searches two of our pages share, keyed as sprint 7 keys them (`competingObservationKey`), from
   * `nearMissSharedSearchKeys`.
   */
  sharedSearchKeys?: ReadonlySet<string>;
}

// ---------------------------------------------------------------------------------------------
// Words

/**
 * Words that say nothing about where a search's subject sits on a page. Small on purpose: a word
 * that names a thing, a format or a task stays, so "free", "template" and "pdf" must be found.
 */
const FUNCTION_WORDS = new Set(
  `a an the and or for to of in on at by is are was were be been with from as it its into about
  your you my me i we our this that these those can could should would will do does did how what why when
  where who which whom vs versus there here than then so if not no`.split(/\s+/).filter(Boolean)
);

/**
 * Verbs any how-to search can carry that name no subject: "how to make a <file> read-only" asks
 * about the file and the setting, and a page that never says "make" still covers it (the labelled
 * set reads these as in the title on both sites). Not small words for the noise rules, which read
 * `FUNCTION_WORDS` only.
 */
const GENERIC_VERBS = new Set(['make', 'makes', 'made', 'get', 'gets', 'got', 'use', 'uses', 'using', 'used']);

/** Accents folded away: "résumé" and "resume" are one word for the location test. */
function foldAccents(text: string): string {
  return String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function rawWords(text: string): string[] {
  return foldAccents(text)
    .toLowerCase()
    .replace(/[’']/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** The search's words that must be found, in order, function words out (all of them when none is left). */
export function nearMissSearchWords(search: string): string[] {
  const all = rawWords(search);
  const content = all.filter((w) => !FUNCTION_WORDS.has(w) && !GENERIC_VERBS.has(w));
  return [...new Set(content.length ? content : all)];
}

/** `stemTopicToken`, except that a stem is never shorter than two letters ("ms" stays "ms", not "m"). */
function stem(word: string): string {
  const s = stemTopicToken(word);
  if (s.length < 2) return word;
  // A content word must not stem into a small word ("notes" → "not", "note" → "not"): it would then
  // match every "not" in a heading. Only its plural "s" goes (sprint 8 C round 2).
  if (FUNCTION_WORDS.has(s) && !FUNCTION_WORDS.has(word)) return word.replace(/s$/, '');
  return s;
}

function stemsOf(text: string): Set<string> {
  return new Set(rawWords(text).map(stem));
}

/**
 * What one stem may add to another and still be the same word: derivations ("present" /
 * "presentation", "estimate" / "estimation", "manage" / "management", "extract" / "extractor"). Whole words otherwise: a
 * bare prefix is not a match ("real" is not "really", "plan" is not "planet").
 */
const DERIVATION_ENDINGS = new Set(['ion', 'ions', 'ation', 'ations', 'ment', 'ments', 'ement', 'ements', 'ed', 'd', 'ery', 'ize', 'ized', 'ise', 'ised']);

/**
 * Endings that also turn a short word into a different one ("port" / "portal", "sign" / "signal",
 * "form" / "formal", "fact" / "factor", "tract" / "tractor"): allowed only after a stem at least this
 * long. "-al" needs 5 letters ("remov" / "removal", "propos" / "proposal"); "-or" needs 6 ("extract" /
 * "extractor", which keeps the 4 labelled "… extractor …" noise searches on SlideSpeak found in the
 * title). A length guard, not a word list: a longer pair of different words ("person" / "personal")
 * still pairs.
 */
const GUARDED_ENDINGS: ReadonlyMap<string, number> = new Map([
  ['al', 5],
  ['or', 6],
  ['ors', 6]
]);

function sameWord(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  if (short.length < 4 || !long.startsWith(short)) return false;
  const rest = long.slice(short.length);
  // A doubled last letter before an ending the stemmer took off ("plann" for "planning").
  const guard = GUARDED_ENDINGS.get(rest);
  if (guard !== undefined) return short.length >= guard;
  return DERIVATION_ENDINGS.has(rest) || rest === short[short.length - 1];
}

function hasWord(pool: Set<string>, word: string): boolean {
  const s = stem(word);
  if (pool.has(s)) return true;
  for (const p of pool) if (sameWord(p, s)) return true;
  return false;
}

/**
 * The words not found in `pool`. Two neighbouring words of the search that our page writes as one
 * ("work flow" for "workflow") count as found; the joined word must be our word exactly, since a
 * prefix match would let any word followed by anything match.
 */
function missingFrom(pool: Set<string>, words: readonly string[]): string[] {
  const found = words.map((w) => hasWord(pool, w));
  for (let i = 0; i + 1 < words.length; i++) {
    if ((!found[i] || !found[i + 1]) && pool.has(stem(words[i] + words[i + 1]))) found[i] = found[i + 1] = true;
  }
  return words.filter((_, i) => !found[i]);
}

/**
 * The word helpers the card's answers read (`near-miss-card.ts`), so the card and the location
 * check agree on what a word, a stem and a match are.
 */
export const nearMissRawWords = rawWords;
export const nearMissStems = stemsOf;
export const nearMissHasWord = hasWord;
export const nearMissMissingFrom = missingFrom;

/** Lower case, letters and digits only, single spaces: the "word for word" test. */
function phrase(text: string): string {
  return rawWords(text).join(' ');
}

// ---------------------------------------------------------------------------------------------
// Where the words sit

function headingCandidates(page: NearMissPageText): Array<{ kind: 'title' | 'h1' | 'heading'; text: string }> {
  const out: Array<{ kind: 'title' | 'h1' | 'heading'; text: string }> = [];
  if (page.title?.trim()) out.push({ kind: 'title', text: page.title.trim() });
  if (page.h1?.trim()) out.push({ kind: 'h1', text: page.h1.trim() });
  for (const b of page.blocks) {
    const h = b.heading.trim();
    if (!h || (page.h1 && phrase(h) === phrase(page.h1))) continue;
    out.push({ kind: b.level === 1 ? 'h1' : 'heading', text: h });
  }
  return out;
}

/** Where the search's words sit on our page (`NearMissWordLocation`). */
export function nearMissWordLocation(search: string, page: NearMissPageText): NearMissWordLocation {
  const words = nearMissSearchWords(search);
  const headings = headingCandidates(page);
  const searchPhrase = phrase(search);
  const phraseInHeading =
    searchPhrase.length > 0 && headings.some((h) => ` ${phrase(h.text)} `.includes(` ${searchPhrase} `));
  const base = { phraseInHeading, missingWords: [] as string[] };
  if (!words.length) return { place: 'not_on_page', heading: null, section: null, ...base };

  // A heading for the reverse conversion ("Y to X" for a search "X to Y") holds the words, not the search.
  const inHeading = headings.find(
    (h) => missingFrom(stemsOf(h.text), words).length === 0 && !searchRunsAgainstPageDirection([h.text], search)
  );
  if (inHeading) return { place: 'heading', heading: inHeading, section: null, ...base };

  const blockText = (b: NearMissPageBlock) => `${b.heading} ${b.body}`;
  const blocks = page.blocks;
  if (blocks.length && missingFrom(stemsOf(blockText(blocks[0])), words).length === 0) {
    return { place: 'opening', heading: null, section: blocks[0].heading.trim(), ...base };
  }
  const section = blocks.slice(1).find((b) => missingFrom(stemsOf(blockText(b)), words).length === 0);
  if (section) return { place: 'one_section', heading: null, section: section.heading.trim(), ...base };

  const whole = stemsOf([page.title ?? '', page.h1 ?? '', ...blocks.map(blockText)].join(' '));
  const missing = missingFrom(whole, words);
  if (!missing.length) return { place: 'spread', heading: null, section: null, ...base };
  return { place: 'not_on_page', heading: null, section: null, phraseInHeading, missingWords: missing };
}

// ---------------------------------------------------------------------------------------------
// Noise

/**
 * A search of at least this many words with not one function word in it is a string of keywords
 * pasted into the box (a scraper, a tool, an ad brief), not a person asking for this page.
 *
 * Sprint 8 frozen data, queue pages, candidates over the floor: ManyRequests' two such strings run 8
 * and 9 words (the labelled set calls both noise); the longest search with no function word that it
 * does not call noise has 6 words (four "<kind of> contract / template + format or 'free'" searches).
 * SlideSpeak has none over 5. Margin: 1 above, 1 below.
 * Site-wide (ManyRequests 416 pages): 25 such strings of 6 words, most of them real ("<kind of>
 * <document> template"), 10 of 7 words or more, read as keyword strings (a family of made-up site
 * addresses, a personal site's address, policy and wording strings).
 */
export const NEAR_MISS_KEYWORD_STRING_MIN_WORDS = 7;

/**
 * Longer than this many words is a chat-style prompt, not a search: sprint 7's own cut
 * (`COMPETING_SEARCH_MAX_WORDS`, 10), imported rather than copied so the two engines agree on what
 * a person's search is. On the sprint 8 queue candidates it removes 6 of the 7 searches the labelled
 * set calls AI-style prompts on ManyRequests (11 to 16 words) and nothing labelled worth doing or
 * maybe; the longest searches it keeps that are labelled at all have 10 words (two "not worth it").
 * SlideSpeak's longest candidate has 10 words. The seventh prompt is a 6-word question with a "?",
 * which no length rule separates from people's short questions. Site-wide (ManyRequests 416 pages)
 * the cut removes 151 searches, nearly all assistant-style, but also a few long questions a person
 * types, and keeps assistant-style families of 9 and 10 words: length is the only rule that holds on
 * the labels, and it does not separate the two kinds (TECHNICAL_DEBT §0cl).
 */
export const NEAR_MISS_PROMPT_MAX_WORDS = COMPETING_SEARCH_MAX_WORDS;

/**
 * "Sample / template / free / example"-style words: they ask for the thing a template or tool page
 * already offers, in other words, so they change nothing about what the page should say. The
 * owner's four (Q3) and "online", which the labelled set calls the same variant on a tool page
 * (SlideSpeak: 7 of 7 "… online" searches on a tool whose title already has the rest). "Format" was
 * tried and left out: its one labelled hit is a "maybe" for wording ("<thing> format" where the page
 * says "template").
 */
const VARIANT_WORDS = new Set(['sample', 'samples', 'template', 'templates', 'example', 'examples', 'free', 'online']);

/** File formats, for "<format> a <format>" (Spanish, Portuguese, Italian, French "to"). */
const FILE_FORMATS = new Set(
  'pdf ppt pptx pps ppsx potx doc docx word excel xls xlsx csv txt rtf odt odp jpg jpeg png gif webp heic svg bmp tiff mp3 mp4 mov wav html epub keynote key powerpoint google slides'.split(' ')
);

/**
 * Small words of the languages searchers on these sites use most after English, that English never
 * uses as words of its own. One is enough: an English search does not contain "para" or "ejemplo".
 * Words that are also English, or part of an English place name, are left out ("a", "de", "die",
 * "come", "con", "per", "pour", "comment", "converter", "los", "las", "del", "fare", "com").
 * "en" (French and Spanish "in": "<format> en <orientation>") is in: English has it only in borrowed
 * phrases ("en route", "en masse") nobody types with a file format (sprint 8 C round 2).
 */
const OTHER_LANGUAGE_WORDS = new Set(
  `para como una por que ejemplo ejemplos plantilla plantillas gratis gratuito gratuita hacer crear convertir
  convertidor uma modelo modelos fazer criar exemplo exemplos
  avec faire gratuit modele creer convertisseur
  und mit wie für fur kostenlos vorlage vorlagen erstellen umwandeln
  het een voor hoe maken gratis
  di creare modello en`.split(/\s+/).filter(Boolean)
);

/**
 * Countries, their adjectives and large cities the sites' searches name, and "near me". Never a
 * language's name ("english", "spanish"): a search for a page in a language is not a local variant,
 * and "other_language" reads the search's own language. A word
 * list on purpose (owner Q3: rules only); it only has to catch the local variant of a search, and
 * only where our page does not name the place itself. No queue candidate on either site names one
 * on the sprint 8 frozen data; site-wide ManyRequests has 4 (a country after "in", or a country code),
 * SlideSpeak none. The rule is the owner's (Q3) and the direction test's (findings §35).
 */
const PLACES = new Set(
  `usa america american uk britain british england canada canadian australia australian india indian
  pakistan nigeria kenya philippines singapore malaysia indonesia dubai uae germany france spain
  italy mexico brazil ireland scotland netherlands europe european asia africa african japan china
  london manchester toronto vancouver sydney melbourne chicago boston seattle austin denver atlanta miami houston dallas
  california texas florida york nyc bangalore mumbai delhi lagos`.split(/\s+/).filter(Boolean)
);
const PLACE_PHRASES = ['near me', 'new york', 'los angeles', 'san francisco', 'new zealand', 'south africa', 'united states', 'united kingdom'];

/**
 * A year in a search that the page's title and H1 do not carry. Sprint 8 frozen data: ManyRequests'
 * queue candidates hold 3 (old years, none labelled); site-wide 14 (old years, and last year's
 * "best …" variants on pages titled for this year). SlideSpeak: none.
 */
const YEAR = /\b(19[5-9]\d|20\d\d)\b/g;

const DOMAIN = /\b([a-z0-9-]+)\.(com|net|org|io|co|ai|app|xyz|info|biz|ru|cn|top|site|online)\b/g;

/**
 * Not one person's search for this page: a search operator, the address of a site our page never
 * names, symbols or digits only, or a long string of keywords. A product's own domain that our page
 * names ("<product>.com vs …", "<product>.ai presentation" on a page about that product) is a real
 * search for the product: on the sprint 8 frozen data, site-wide, every such search names a tool the
 * page compares (ManyRequests 5, SlideSpeak 5), and every domain our page never names is a made-up
 * site address or a personal site (ManyRequests 7, SlideSpeak 0).
 */
function isSpam(search: string, page: NearMissPageText): boolean {
  const s = search.toLowerCase();
  if (/\b(site|inurl|intitle|related|cache):/.test(s) || /https?:|www\./.test(s)) return true;
  const domains = [...s.matchAll(DOMAIN)].map((m) => m[1]);
  if (domains.length) {
    const own = pageWords(page);
    if (domains.some((d) => !rawWords(d).every((w) => own.has(w)))) return true;
  }
  const words = rawWords(s);
  if (!words.length || words.every((w) => /^\d+$/.test(w))) return true;
  return words.length >= NEAR_MISS_KEYWORD_STRING_MIN_WORDS && !words.some((w) => FUNCTION_WORDS.has(w));
}

function isPrompt(search: string): boolean {
  return rawWords(search).length > NEAR_MISS_PROMPT_MAX_WORDS;
}

/**
 * English words written with their accents. An accented word that folds to one of these is English
 * ("résumé template"); any other accented word is another language.
 */
const ENGLISH_ACCENTED_WORDS = new Set(
  'resume resumes cafe cafes naive cliche cliches deja vu protege fiance fiancee facade creme decor entree expose soiree eclair jalapeno pinata rose saute touche emigre souffle'.split(' ')
);

function isOtherLanguage(search: string): boolean {
  // Another script: letters beyond Latin.
  if (/[^\x00-\x7f\u00c0-\u024f’‘“”–—…]/.test(search)) return true;
  // Accented Latin, unless it is an English word written with its accents.
  for (const token of String(search).toLowerCase().split(/[^a-z0-9\u00c0-\u024f]+/)) {
    if (/[\u00c0-\u024f]/.test(token) && !ENGLISH_ACCENTED_WORDS.has(foldAccents(token))) return true;
  }
  const words = rawWords(search);
  if (words.some((w) => OTHER_LANGUAGE_WORDS.has(w))) return true;
  // "<format> a <format>": a format, "a", a format, in a search with no other English small word ("how to
  // make <format> a <format>" is English).
  if (words.some((w) => w !== 'a' && FUNCTION_WORDS.has(w))) return false;
  for (let i = 1; i + 1 < words.length; i++) {
    if (words[i] === 'a' && FILE_FORMATS.has(words[i - 1]) && FILE_FORMATS.has(words[i + 1])) return true;
  }
  return false;
}

function headingTexts(page: NearMissPageText): string {
  return [page.title ?? '', page.h1 ?? ''].join(' ');
}

function isYearVariant(search: string, page: NearMissPageText): boolean {
  const years = search.match(YEAR);
  if (!years) return false;
  const carried = new Set(headingTexts(page).match(YEAR) ?? []);
  return years.some((y) => !carried.has(y));
}

function pageWords(page: NearMissPageText): Set<string> {
  return new Set(rawWords([page.title ?? '', page.h1 ?? '', ...page.blocks.map((b) => `${b.heading} ${b.body}`)].join(' ')));
}

function isPlaceVariant(search: string, page: NearMissPageText): boolean {
  const words = rawWords(search);
  const text = ` ${words.join(' ')} `;
  const named = [
    ...words.filter((w) => PLACES.has(w)),
    ...PLACE_PHRASES.filter((p) => text.includes(` ${p} `))
  ];
  if (!named.length) return false;
  const own = pageWords(page);
  const ownText = ` ${[...rawWords([page.title ?? '', page.h1 ?? '', ...page.blocks.map((b) => `${b.heading} ${b.body}`)].join(' '))].join(' ')} `;
  return named.some((p) => (p.includes(' ') ? !ownText.includes(` ${p} `) : !own.has(p)));
}

/**
 * The search is the page's title or H1 plus a "sample / template / free / example"-style word, on
 * a page that offers the thing (its title or H1 carries such a word itself: a template, a free
 * online tool). The page already answers it word for word; being at #4–20 for it is an authority
 * problem, not a gap (findings §35). An article whose title has no such word ("<things>: how to use
 * them") is not offering the thing, so "<thing> example" there asks for content it may lack (the
 * labelled set has one such search worth doing on ManyRequests; this condition keeps it). When the
 * title or H1 holds every word of the search, the variant word too, it is `title_match`; otherwise
 * `answered_variant`.
 */
function answeredVariantKind(search: string, page: NearMissPageText): 'title_match' | 'answered_variant' | null {
  // "How to …" asks for the steps, not the thing the page offers: on a tool page it is the labelled
  // set's worth-doing pattern (a short manual how-to above the tool), never a variant of the title.
  if (/^\s*how\s+to\b/i.test(search)) return null;
  const words = nearMissSearchWords(search);
  if (!words.some((w) => VARIANT_WORDS.has(w))) return null;
  const core = words.filter((w) => !VARIANT_WORDS.has(w));
  if (!core.length) return null;
  let kind: 'title_match' | 'answered_variant' | null = null;
  for (const h of [page.title, page.h1]) {
    if (!h || !rawWords(h).some((w) => VARIANT_WORDS.has(w))) continue;
    const pool = stemsOf(h);
    if (missingFrom(pool, core).length) continue;
    // Every word, the variant words too, in that title or H1: the search is the title.
    if (!missingFrom(pool, words).length) return 'title_match';
    kind = 'answered_variant';
  }
  return kind;
}

/** The noise rules that apply to one search, in `NEAR_MISS_REMOVAL_REASONS` order. */
export function nearMissNoiseReasons(search: string, page: NearMissPageText): NearMissRemovalReason[] {
  const out: NearMissRemovalReason[] = [];
  if (isSpam(search, page)) out.push('spam');
  if (isPrompt(search)) out.push('ai_prompt');
  if (isOtherLanguage(search)) out.push('other_language');
  if (isYearVariant(search, page)) out.push('year_variant');
  if (isPlaceVariant(search, page)) out.push('place_variant');
  const answered = answeredVariantKind(search, page);
  if (answered) out.push(answered);
  return out;
}

// ---------------------------------------------------------------------------------------------
// Shared searches (owner Q5)

/**
 * page id → the shared searches the Cannibalization card owns on that page, keyed as sprint 7 keys
 * them. Owned means: the search is contested in a pair the card actually shows (sprint 7's own
 * decision, `competingPairVerdicts` then `cannibalizationVerdictsForPage`: a verdict not set aside),
 * on either page of the pair. Owner Q5's reason is that two cards must not give two answers for one
 * search; where no card shows the pair (keep both, set aside, excluded), nobody else answers the
 * search, so the near-miss card may.
 *
 * Read from the engine's full result, not the card's payload, which lists only the first
 * `CANNIBALIZATION_PAYLOAD_SEARCHES` of a pair's contested searches.
 *
 * Sprint 8 frozen data, queue pages: the earlier reading (contested in any pair the engine reports)
 * removed 5 ManyRequests and 3 SlideSpeak candidates; site-wide 189 and 104, of which 79 and 78 sat
 * in pairs no card shows, and the independent grade called several of those removals wrong. This
 * reading removes 0 queue candidates on either site, and 5 (ManyRequests) and 4 (SlideSpeak) site-wide:
 * the card shows merges only (sprint 7 sets every differentiate verdict aside).
 */
export function nearMissCardOwnedSearchKeys(result: CompetingPagesResult | null | undefined): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  if (!result) return out;
  const payload = competingPagesPayload(result, null);
  const verdicts = competingPairVerdicts(payload.pairs, payload.texts);
  const full = new Map(result.pairs.map((p) => [`${p.a.pageId}|${p.b.pageId}`, p]));
  const pageIds = new Set(payload.pairs.flatMap((p) => [p.a.pageId, p.b.pageId]));
  for (const pageId of pageIds) {
    for (const v of cannibalizationVerdictsForPage(verdicts, pageId)) {
      const pair = full.get(`${v.pair.a.pageId}|${v.pair.b.pageId}`);
      if (!pair) continue;
      let keys = out.get(pageId);
      if (!keys) out.set(pageId, (keys = new Set()));
      for (const sh of pair.sharedSearches) if (sh.contested) keys.add(sh.key);
    }
  }
  return out;
}

/** One page's card-owned shared searches (`nearMissCardOwnedSearchKeys`). */
export function nearMissSharedSearchKeys(result: CompetingPagesResult | null | undefined, pageId: string): Set<string> {
  return nearMissCardOwnedSearchKeys(result).get(pageId) ?? new Set();
}

// ---------------------------------------------------------------------------------------------
// The page's near misses

const demandOf = (source: NearMissSource, s: NearMissSearchInput): number =>
  Math.max(0, Number(source === 'search_console' ? s.impressions : s.searchVolume) || 0);

export function nearMissInBand(position: number | null | undefined): boolean {
  return typeof position === 'number' && position >= NEAR_MISS_MIN_POSITION && position <= NEAR_MISS_MAX_POSITION;
}

export function nearMissOverFloor(source: NearMissSource, s: NearMissSearchInput): boolean {
  return demandOf(source, s) >= (source === 'search_console' ? NEAR_MISS_MIN_IMPRESSIONS : NEAR_MISS_MIN_SEARCH_VOLUME);
}

/** Every near-miss search of one page, kept and removed, most demand first. Pure, deterministic. */
export function buildNearMissSearches(input: NearMissInput): NearMissPageResult {
  const estimated = input.source !== 'search_console';
  const inBand = input.searches.filter((s) => nearMissInBand(s.position));
  const removedBy = Object.fromEntries(NEAR_MISS_REMOVAL_REASONS.map((r) => [r, 0])) as Record<NearMissRemovalReason, number>;
  const searches: NearMissSearch[] = inBand
    .filter((s) => nearMissOverFloor(input.source, s))
    .map((s) => {
      const reasons = nearMissNoiseReasons(s.search, input.page);
      const key = competingObservationKey(input.source, s.search);
      if (key && input.sharedSearchKeys?.has(key)) reasons.push('shared_with_our_page');
      const reason = reasons[0] ?? null;
      if (reason) removedBy[reason] += 1;
      return {
        search: s.search,
        source: input.source,
        estimated,
        position: s.position as number,
        impressions: estimated ? null : s.impressions ?? 0,
        clicks: estimated ? null : s.clicks ?? 0,
        searchVolume: estimated ? s.searchVolume ?? 0 : null,
        demand: demandOf(input.source, s),
        kept: reason === null,
        reason,
        reasons,
        location: nearMissWordLocation(s.search, input.page)
      };
    })
    .sort((x, y) => y.demand - x.demand || x.position - y.position || (x.search < y.search ? -1 : x.search > y.search ? 1 : 0));
  return {
    source: input.source,
    estimated,
    warning: estimated ? ESTIMATED_SEARCH_EVIDENCE_WARNING : null,
    title: input.page.title,
    h1: input.page.h1,
    headings: input.page.blocks.map((b) => b.heading.trim()).filter(Boolean),
    inBand: inBand.length,
    searches,
    removedBy,
    kept: searches.filter((s) => s.kept).length
  };
}

// ---------------------------------------------------------------------------------------------
// Our page's text

function blockList(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  return value && typeof value === 'object' ? [value] : [];
}

/**
 * Our page's title, H1 and main-content blocks from the stored analysis, with the blocks
 * `isTemplateBlock` calls our site's template left out (`site-template-blocks.ts` on the server).
 * Text that is only a link (a related-post card, the nav) is not the page covering anything, as in
 * `ownPagePassages`.
 */
export function nearMissPageText(analysis: any, isTemplateBlock?: (block: unknown) => boolean): NearMissPageText {
  const meta = analysis?.rawData?.pageData?.meta;
  const title = typeof meta?.title === 'string' && meta.title.trim() ? meta.title.replace(/\s+/g, ' ').trim() : null;
  const h1Raw = Array.isArray(meta?.htags?.h1) ? meta.htags.h1[0] : null;
  const h1 = typeof h1Raw === 'string' && h1Raw.trim() ? h1Raw.replace(/\s+/g, ' ').trim() : null;
  const blocks: NearMissPageBlock[] = [];
  const sc = analysis?.rawData?.structuredContent;
  if (sc && typeof sc === 'object') {
    // A line that is word for word one of the page's headings is a table-of-contents entry (the crawler
    // files the contents list inside the opening block), not text that covers anything.
    const headingPhrases = new Set<string>();
    for (const part of ['main_topic', 'secondary_topic']) {
      for (const block of blockList(sc[part])) {
        const h = phrase(String(block?.h_title ?? ''));
        if (h) headingPhrases.add(h);
      }
    }
    for (const part of ['main_topic', 'secondary_topic']) {
      for (const block of blockList(sc[part])) {
        if (isTemplateBlock?.(block)) continue;
        const heading = String(block?.h_title ?? '').replace(/\s+/g, ' ').trim();
        const texts: string[] = [];
        for (const key of ['primary_content', 'secondary_content']) {
          for (const item of blockList(block?.[key])) {
            if (item?.url) continue;
            if (typeof item?.text !== 'string') continue;
            const line = phrase(item.text);
            if (line && line !== phrase(heading) && headingPhrases.has(line)) continue;
            texts.push(item.text);
          }
        }
        for (const table of blockList(block?.table_content)) {
          for (const key of ['header', 'body', 'footer']) {
            for (const row of blockList(table?.[key])) {
              texts.push(blockList(row?.row_cells).map((c) => String(c?.text ?? '')).join(' | '));
            }
          }
        }
        const body = texts.join(' ').replace(/\s+/g, ' ').trim();
        if (!heading && !body) continue;
        blocks.push({ heading, level: typeof block?.level === 'number' ? block.level : null, body });
      }
    }
  }
  return { title, h1, blocks };
}
